import { Socket as SocketClient } from 'socket.io-client'
import { ContactInfo, LiveCallLog } from 'store/types/lead'
import { fetchLiveCallByCallLogId } from './apis'
import { EventEmitter } from 'events'
import { intlToLocalNumberV2 } from './utils'

interface CallReadyEvent {}
interface CallCreatedEvent {
  leadId: string | null
  liveCallLog: LiveCallLog
}
interface CallRingEvent {
  leadId: string | null
}
interface CallAnswerEvent {
  leadId: string | null
}
interface CallHangEvent {
  liveCallLog: LiveCallLog
}

export enum SessionState {
  IDLE = 'IDLE',
  CREATED = 'CREATED',
  INITIALIZE = 'INITIALIZE',
  DIALING_SOURCE = 'DIALING_SOURCE',
  SOURCE_ANSWERED = 'SOURCE_ANSWERED',
  DIALING_DESTINATION = 'DIALING_DESTINATION',
  DESTINATION_ANSWERED = 'DESTINATION_ANSWERED',
  SOURCE_HANGUP = 'SOURCE_HANGUP',
  DESTINATION_HANGUP = 'DESTINATION_HANGUP',
  FAIL = 'FAIL',
  END = 'END',
}

type SessionStatePayload =
  | {
      type:
        | SessionState.IDLE
        | SessionState.INITIALIZE
        | SessionState.DIALING_SOURCE
        | SessionState.SOURCE_ANSWERED
        | SessionState.DIALING_DESTINATION
        | SessionState.DESTINATION_ANSWERED
        | SessionState.SOURCE_HANGUP
    }
  | { type: SessionState.CREATED; callLogId: string }
  | { type: SessionState.DESTINATION_HANGUP | SessionState.FAIL; cause: string; causeTxt: string }
  | { type: SessionState.END; isConnected: boolean; isAnswered: boolean }

interface Extra {
  user_id?: string
  lead_id?: string
  contact_info_id?: string
  [key: string]: unknown
}

export interface SessionStateResponse {
  action: SessionState
  state: SessionState
  callLogId: string
  originateMessage?: string
  extra?: Extra
}

interface MakeCallRequest {
  destination: string
  extra?: Extra
}

export interface MakeCallResponse {
  success: boolean
  message: string
  callLogId: string
}

export default class DialerClient extends EventEmitter {
  private leadId: string | null = null

  private boundWebhookNotifyHandler: ((res: SessionStateResponse) => void) | undefined
  private boundSessionStateHandler: ((res: SessionStatePayload) => void) | undefined

  constructor(private socket: SocketClient) {
    super()
    this.registerListeners(this.socket)
  }

  private registerListeners(socket: SocketClient) {
    this.boundSessionStateHandler = this.sessionStateHandler.bind(this)
    socket.on('session:state', this.boundSessionStateHandler)
  }

  private async sessionStateHandler(res: SessionStatePayload) {
    switch (res.type) {
      case SessionState.END:
      case SessionState.IDLE:
        this.emit('call_ready', {} as CallReadyEvent)
        break
      case SessionState.INITIALIZE:
        this.emit('call_initialize', {})
        break
      case SessionState.CREATED:
        const liveCallLog = await fetchLiveCallByCallLogId(res.callLogId)
        if (liveCallLog) this.emit('call_created', { liveCallLog } as CallCreatedEvent)
        break
      case SessionState.DIALING_DESTINATION:
      case SessionState.DIALING_SOURCE:
        this.emit('call_ring', { leadId: this.leadId } as CallRingEvent)
        break
      case SessionState.DESTINATION_ANSWERED:
        this.emit('call_answer', { leadId: this.leadId } as CallAnswerEvent)
        break
      case SessionState.SOURCE_HANGUP:
        this.emit('call_hang', {})
        break
    }
  }

  async makeCall(leadId: string, contactInfo: ContactInfo, extra: Record<string, unknown>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.socket) {
        const destinationNumber = intlToLocalNumberV2(contactInfo.contactValue)
        const payload: MakeCallRequest = {
          destination: destinationNumber,
          extra: {
            lead_id: leadId,
            ...extra,
          },
        }
        this.socket.emit('call:make_call', payload, async ({ success, message }: MakeCallResponse) => {
          if (!success) {
            reject(new Error(message))
          }
          this.leadId = leadId
          resolve()
        })
      }
    })
  }

  destory(): void {
    this.socket.off('webhook:notified', this.boundWebhookNotifyHandler)
    if (this.boundSessionStateHandler) {
      this.socket.off('session:state', this.boundSessionStateHandler)
    }
  }
}
